if (!customElements.get('ondelie-pdp-hero')) {
  customElements.define(
    'ondelie-pdp-hero',
    class OndeliePdpHero extends HTMLElement {
      constructor() {
        super();
        this.observer = null;
        this.quantity = 1;
        this.unitPriceCents = 0;
        this.handleClick = this.handleClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleGalleryScroll = this.handleGalleryScroll.bind(this);
        this.updateStickyAtc = this.updateStickyAtc.bind(this);
        this.galleryScrollFrame = null;
      }

      connectedCallback() {
        this.addEventListener('click', this.handleClick);
        this.addEventListener('keydown', this.handleKeydown);
        this.galleryStage = this.querySelector('[data-oph-gallery-stage]');
        if (this.galleryStage) {
          this.galleryStage.addEventListener('scroll', this.handleGalleryScroll, {passive: true});
        }
        window.addEventListener('scroll', this.updateStickyAtc, {passive: true});
        window.addEventListener('resize', this.updateStickyAtc, {passive: true});
        this.classList.add('is-mounted');
        this.revealTimer = window.setTimeout(() => this.classList.add('is-visible'), 120);

        const activeVariant = this.querySelector('[data-oph-variant].is-selected');
        const activeMode = this.querySelector('[data-oph-purchase-mode].is-selected')?.dataset.ophPurchaseMode;
        const activePlan = activeMode === 'subscription' ? this.getActivePlan() : null;
        this.unitPriceCents = Number(
          activePlan?.dataset.priceCents ||
          activeVariant?.dataset.priceCents ||
          this.dataset.initialUnitPrice ||
          0
        );
        this.updateStickyMode(activeMode === 'subscription');

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.classList.add('is-visible');
        } else if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (!entries[0].isIntersecting) return;
              this.classList.add('is-visible');
              this.observer.disconnect();
            },
            {rootMargin: '0px 0px -5% 0px', threshold: 0}
          );
          this.observer.observe(this);
        } else {
          this.classList.add('is-visible');
        }

        this.updateStickyAtc();
      }

      handleClick(event) {
        const thumbnail = event.target.closest('[data-oph-thumbnail]');
        const variant = event.target.closest('[data-oph-variant]');
        const purchaseMode = event.target.closest('[data-oph-purchase-mode]');
        const plan = event.target.closest('[data-oph-plan]');
        const quantityMinus = event.target.closest('[data-oph-quantity-minus]');
        const quantityPlus = event.target.closest('[data-oph-quantity-plus]');
        const accordionButton = event.target.closest('[data-oph-accordion-button]');
        const aiPrompt = event.target.closest('[data-oph-ai-prompt]');
        const aiSubmit = event.target.closest('[data-oph-ai-submit]');
        const stickySubmit = event.target.closest('[data-oph-sticky-submit]');

        if (thumbnail && this.contains(thumbnail)) this.selectMedia(thumbnail.dataset.ophThumbnail);
        if (variant && this.contains(variant)) this.selectVariant(variant);
        if (purchaseMode && this.contains(purchaseMode)) this.selectPurchaseMode(purchaseMode);
        if (plan && this.contains(plan)) this.selectPlan(plan);
        if (quantityMinus && this.contains(quantityMinus)) this.updateQuantity(-1);
        if (quantityPlus && this.contains(quantityPlus)) this.updateQuantity(1);
        if (accordionButton && this.contains(accordionButton)) this.toggleAccordion(accordionButton);
        if (aiPrompt && this.contains(aiPrompt)) this.answerPrompt(aiPrompt);
        if (aiSubmit && this.contains(aiSubmit)) this.submitAi(aiSubmit.closest('[data-oph-ai-box]'));
        if (stickySubmit && this.contains(stickySubmit)) this.submitStickyAtc();
      }

      handleKeydown(event) {
        const input = event.target.closest('[data-oph-ai-input]');
        if (input && event.key === 'Enter') {
          event.preventDefault();
          this.submitAi(input.closest('[data-oph-ai-box]'));
          return;
        }

        const thumb = event.target.closest('[data-oph-thumbnail]');
        if (!thumb || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

        const thumbs = Array.from(this.querySelectorAll('[data-oph-thumbnail]'));
        const currentIndex = thumbs.indexOf(thumb);
        let nextIndex = currentIndex;
        if (event.key === 'ArrowLeft') nextIndex = Math.max(0, currentIndex - 1);
        if (event.key === 'ArrowRight') nextIndex = Math.min(thumbs.length - 1, currentIndex + 1);
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = thumbs.length - 1;
        if (nextIndex === currentIndex) return;

        event.preventDefault();
        thumbs[nextIndex].focus();
        this.selectMedia(thumbs[nextIndex].dataset.ophThumbnail);
      }

      selectMedia(mediaId) {
        if (!mediaId) return;

        let selectedMedia = null;
        this.querySelectorAll('[data-oph-media]').forEach((item) => {
          const isActive = item.dataset.ophMedia === String(mediaId);
          item.classList.toggle('is-active', isActive);
          item.setAttribute('aria-current', isActive ? 'true' : 'false');
          if (isActive) selectedMedia = item;
          if (!isActive) {
            item.querySelectorAll('video').forEach((video) => video.pause());
          }
        });

        this.querySelectorAll('[data-oph-thumbnail]').forEach((button) => {
          const isActive = button.dataset.ophThumbnail === String(mediaId);
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-selected', isActive ? 'true' : 'false');
          button.tabIndex = isActive ? 0 : -1;
        });

        if (!selectedMedia) return;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (window.matchMedia('(max-width: 989px)').matches && this.galleryStage) {
          this.galleryStage.scrollTo({
            left: selectedMedia.offsetLeft,
            behavior: reducedMotion ? 'auto' : 'smooth'
          });
        } else {
          selectedMedia.scrollIntoView({
            behavior: reducedMotion ? 'auto' : 'smooth',
            block: 'start'
          });
        }
      }

      selectVariant(selected) {
        const variantId = selected.dataset.variantId;
        const hasPlans = selected.dataset.hasPlans === 'true';

        this.setPressed(this.querySelectorAll('[data-oph-variant]'), selected);
        this.querySelectorAll('[data-oph-variant-input]').forEach((input) => {
          input.value = variantId;
        });

        this.querySelectorAll('[data-oph-plan-set]').forEach((set) => {
          set.hidden = set.dataset.ophPlanSet !== variantId;
        });

        const subscriptionButton = this.querySelector('[data-oph-purchase-mode="subscription"]');
        if (subscriptionButton) {
          subscriptionButton.disabled = !hasPlans;
          subscriptionButton.setAttribute('aria-disabled', hasPlans ? 'false' : 'true');
        }

        this.updatePurchasePrices(selected);
        this.updateAvailability(selected.dataset.available === 'true');
        this.updateStickyVariant(selected);

        const currentMode = this.querySelector('[data-oph-purchase-mode].is-selected')?.dataset.ophPurchaseMode;
        if (currentMode === 'subscription' && hasPlans) {
          const activeSet = this.getActivePlanSet();
          const activePlan = activeSet?.querySelector('[data-oph-plan].is-selected') || activeSet?.querySelector('[data-oph-plan]');
          if (activePlan) this.selectPlan(activePlan);
        } else {
          const oneTime = this.querySelector('[data-oph-purchase-mode="one-time"]');
          if (oneTime) this.selectPurchaseMode(oneTime);
          else this.applyPrice(selected.dataset.price, selected.dataset.compare, selected.dataset.priceCents, '');
        }

        if (selected.dataset.mediaId) this.selectMedia(selected.dataset.mediaId);

        if (this.dataset.updateUrl === 'true' && this.dataset.productUrl && window.history?.replaceState) {
          const url = `${this.dataset.productUrl}?variant=${variantId}`;
          window.history.replaceState({path: url}, '', url);
        }
      }

      updatePurchasePrices(variant) {
        const oneTimePrice = this.querySelector('[data-oph-one-time-price]');
        if (oneTimePrice) oneTimePrice.textContent = variant.dataset.price || '';

        const oneTimeCompare = this.querySelector('[data-oph-one-time-compare]');
        if (oneTimeCompare) {
          oneTimeCompare.textContent = variant.dataset.compare || '';
          oneTimeCompare.hidden = !variant.dataset.compare;
        }

        const planSet = this.getActivePlanSet();
        const firstPlan = planSet?.querySelector('[data-oph-plan]');
        const subscriptionPrice = this.querySelector('[data-oph-subscription-price]');
        const subscriptionCompare = this.querySelector('[data-oph-subscription-compare]');
        const subscriptionSaving = this.querySelector('[data-oph-subscription-saving]');

        if (subscriptionPrice) subscriptionPrice.textContent = firstPlan?.dataset.price || '';
        if (subscriptionCompare) subscriptionCompare.textContent = variant.dataset.price || '';
        if (subscriptionSaving) {
          subscriptionSaving.textContent = firstPlan?.dataset.saving
            ? `Save ${firstPlan.dataset.saving}%`
            : '';
        }
      }

      selectPurchaseMode(selected) {
        if (selected.disabled) return;

        this.setPressed(this.querySelectorAll('[data-oph-purchase-mode]'), selected);
        this.querySelectorAll('.oph-purchase-card').forEach((card) => {
          card.classList.toggle('is-selected', card.contains(selected));
        });
        const isSubscription = selected.dataset.ophPurchaseMode === 'subscription';
        const planPicker = this.querySelector('[data-oph-plan-picker]');
        const sellingPlanInput = this.querySelector('[data-oph-selling-plan]');
        this.updateStickyMode(isSubscription);

        if (planPicker) planPicker.hidden = !isSubscription;

        if (isSubscription) {
          const activeSet = this.getActivePlanSet();
          const activePlan = activeSet?.querySelector('[data-oph-plan].is-selected') || activeSet?.querySelector('[data-oph-plan]');
          if (activePlan) this.selectPlan(activePlan);
          return;
        }

        if (sellingPlanInput) {
          sellingPlanInput.disabled = true;
          sellingPlanInput.value = '';
        }

        const variant = this.querySelector('[data-oph-variant].is-selected');
        if (variant) {
          this.applyPrice(variant.dataset.price, variant.dataset.compare, variant.dataset.priceCents, '');
        }
      }

      selectPlan(selected) {
        const set = selected.closest('[data-oph-plan-set]');
        if (!set) return;
        this.setPressed(set.querySelectorAll('[data-oph-plan]'), selected);

        const sellingPlanInput = this.querySelector('[data-oph-selling-plan]');
        if (sellingPlanInput) {
          sellingPlanInput.disabled = false;
          sellingPlanInput.value = selected.dataset.planId;
        }

        const subscriptionPrice = this.querySelector('[data-oph-subscription-price]');
        const subscriptionSaving = this.querySelector('[data-oph-subscription-saving]');
        if (subscriptionPrice) subscriptionPrice.textContent = selected.dataset.price || '';
        if (subscriptionSaving) {
          subscriptionSaving.textContent = selected.dataset.saving
            ? `Save ${selected.dataset.saving}%`
            : '';
        }

        this.applyPrice(
          selected.dataset.price,
          selected.dataset.compare,
          selected.dataset.priceCents,
          selected.dataset.saving
        );
      }

      applyPrice(price, compare, cents, saving) {
        const priceElement = this.querySelector('[data-oph-price]');
        const compareElement = this.querySelector('[data-oph-compare]');
        const savingElement = this.querySelector('[data-oph-saving]');

        if (priceElement && price) priceElement.textContent = price;
        if (compareElement) {
          compareElement.textContent = compare || '';
          compareElement.hidden = !compare;
        }
        if (savingElement) {
          savingElement.textContent = saving ? `Save ${saving}%` : '';
          savingElement.hidden = !saving;
        }

        const stickyPrice = this.querySelector('[data-oph-sticky-price]');
        if (stickyPrice && price) stickyPrice.textContent = price;

        this.unitPriceCents = Number(cents || 0);
        this.updateButtonPrice();
      }

      updateAvailability(isAvailable) {
        const button = this.querySelector('[data-add-to-cart]');
        const text = this.querySelector('[data-add-to-cart-text]');
        const stickyButton = this.querySelector('[data-oph-sticky-submit]');
        const stickyLabel = this.querySelector('[data-oph-sticky-label]');
        if (!button) return;

        button.disabled = !isAvailable;
        if (stickyButton) stickyButton.disabled = !isAvailable;
        if (stickyLabel) {
          stickyLabel.textContent = isAvailable
            ? stickyLabel.dataset.availableText
            : stickyLabel.dataset.soldOutText;
        }
        if (text) {
          text.textContent = isAvailable
            ? text.dataset.availableText
            : text.dataset.soldOutText;
        }
      }

      updateQuantity(change) {
        this.quantity = Math.min(99, Math.max(1, this.quantity + change));
        const input = this.querySelector('[data-oph-quantity-input]');
        const output = this.querySelector('[data-oph-quantity-output]');
        if (input) input.value = this.quantity;
        if (output) output.textContent = this.quantity;
        this.updateButtonPrice();
      }

      updateButtonPrice() {
        const element = this.querySelector('[data-oph-button-price]');
        if (!element || !this.unitPriceCents) return;
        const totalPrice = this.formatMoney(this.unitPriceCents * this.quantity);
        element.textContent = `— ${totalPrice}`;
        const stickyPrice = this.querySelector('[data-oph-sticky-price]');
        if (stickyPrice) stickyPrice.textContent = totalPrice;
      }

      updateStickyVariant(variant) {
        const variantLabel = this.querySelector('[data-oph-sticky-variant]');
        const stickyImage = this.querySelector('.oph-sticky-atc__media img');
        if (variantLabel && variant.dataset.variantTitle) {
          variantLabel.textContent = variant.dataset.variantTitle;
        }
        if (stickyImage && variant.dataset.mediaImage) {
          stickyImage.src = variant.dataset.mediaImage;
          stickyImage.srcset = '';
        }
      }

      updateStickyMode(isSubscription) {
        const mode = this.querySelector('[data-oph-sticky-mode]');
        const note = this.querySelector('[data-oph-sticky-price-note]');
        const key = isSubscription ? 'subscriptionText' : 'oneTimeText';
        if (mode) mode.textContent = mode.dataset[key] || '';
        if (note) note.textContent = note.dataset[key] || '';
      }

      submitStickyAtc() {
        const primaryButton = this.querySelector('[data-add-to-cart]');
        if (!primaryButton || primaryButton.disabled) return;
        primaryButton.click();
      }

      updateStickyAtc() {
        const sticky = this.querySelector('[data-oph-sticky-atc]');
        const primaryButton = this.querySelector('[data-add-to-cart]');
        if (!sticky || !primaryButton) return;

        const primaryRect = primaryButton.getBoundingClientRect();
        const revealLine = Math.min(120, window.innerHeight * .18);
        const shouldShow = primaryRect.bottom <= revealLine;
        sticky.classList.toggle('is-visible', shouldShow);
        sticky.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      }

      handleGalleryScroll() {
        if (!this.galleryStage || !window.matchMedia('(max-width: 989px)').matches) return;
        if (this.galleryScrollFrame) cancelAnimationFrame(this.galleryScrollFrame);

        this.galleryScrollFrame = requestAnimationFrame(() => {
          const center = this.galleryStage.scrollLeft + (this.galleryStage.clientWidth / 2);
          const items = Array.from(this.querySelectorAll('[data-oph-media]'));
          const active = items.reduce((nearest, item) => {
            const itemCenter = item.offsetLeft + (item.offsetWidth / 2);
            if (!nearest || Math.abs(itemCenter - center) < nearest.distance) {
              return {item, distance: Math.abs(itemCenter - center)};
            }
            return nearest;
          }, null)?.item;
          if (!active) return;

          items.forEach((item) => {
            const isActive = item === active;
            item.classList.toggle('is-active', isActive);
            item.setAttribute('aria-current', isActive ? 'true' : 'false');
          });
          this.querySelectorAll('[data-oph-thumbnail]').forEach((button) => {
            const isActive = button.dataset.ophThumbnail === active.dataset.ophMedia;
            button.classList.toggle('is-active', isActive);
            button.setAttribute('aria-selected', isActive ? 'true' : 'false');
            button.tabIndex = isActive ? 0 : -1;
          });
        });
      }

      formatMoney(cents) {
        if (window.theme?.formatMoney) {
          return window.theme.formatMoney(cents, this.dataset.moneyFormat);
        }

        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'GBP'
        }).format(cents / 100);
      }

      toggleAccordion(button) {
        const item = button.closest('[data-oph-accordion]');
        const panel = item?.querySelector('.oph-accordion__panel');
        if (!item || !panel) return;

        const willOpen = button.getAttribute('aria-expanded') !== 'true';
        item.classList.toggle('is-open', willOpen);
        button.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        panel.hidden = !willOpen;
      }

      answerPrompt(button) {
        const box = button.closest('[data-oph-ai-box]');
        const input = box?.querySelector('[data-oph-ai-input]');
        const answer = box?.querySelector('[data-oph-ai-answer]');
        if (!box || !answer) return;

        if (input) input.value = button.textContent.trim();
        answer.textContent = button.dataset.answer || answer.dataset.fallback || '';
        answer.hidden = false;
      }

      submitAi(box) {
        const input = box?.querySelector('[data-oph-ai-input]');
        const answer = box?.querySelector('[data-oph-ai-answer]');
        if (!box || !answer || !input?.value.trim()) return;

        answer.textContent = answer.dataset.fallback || '';
        answer.hidden = false;
      }

      getActivePlanSet() {
        return Array.from(this.querySelectorAll('[data-oph-plan-set]')).find((set) => !set.hidden);
      }

      getActivePlan() {
        const set = this.getActivePlanSet();
        return set?.querySelector('[data-oph-plan].is-selected') || set?.querySelector('[data-oph-plan]');
      }

      setPressed(elements, selected) {
        Array.from(elements).forEach((element) => {
          const isSelected = element === selected;
          element.classList.toggle('is-selected', isSelected);
          element.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('keydown', this.handleKeydown);
        if (this.galleryStage) {
          this.galleryStage.removeEventListener('scroll', this.handleGalleryScroll);
        }
        window.removeEventListener('scroll', this.updateStickyAtc);
        window.removeEventListener('resize', this.updateStickyAtc);
        if (this.galleryScrollFrame) cancelAnimationFrame(this.galleryScrollFrame);
        if (this.revealTimer) window.clearTimeout(this.revealTimer);
        if (this.observer) this.observer.disconnect();
      }
    }
  );
}
